import { useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, Sliders, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRODUCT_CATEGORIES, categoryLabel } from "@/lib/productCategories";

function SortableHead({ field, current, order, onSort, children, className = "" }) {
  const active = current === field;
  const Icon = !active ? ArrowUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return <TableHead className={className}>
    <button type="button" className="flex items-center gap-1.5 whitespace-nowrap hover:text-foreground" onClick={() => onSort(field)}>
      {children}<Icon className="h-3.5 w-3.5" />
    </button>
  </TableHead>;
}

export default function Inventory() {
  const { t, fmtNumber } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [adj, setAdj] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("true");
  const [category, setCategory] = useState("all");
  const [vehicleMake, setVehicleMake] = useState("all");
  const [vehicleModel, setVehicleModel] = useState("all");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const { data = { items: [], total: 0 }, isLoading, isFetching } = useQuery({
    queryKey: ["balances", q, page, pageSize, status, category, vehicleMake, vehicleModel, sort, order],
    queryFn: async () => (await api.get("/inventory/balances", { params: {
      q, page, page_size: pageSize, sort, order, is_active: status === "all" ? "" : status,
      category: category === "all" ? "" : category,
      vehicle_make: vehicleMake === "all" ? "" : vehicleMake,
      vehicle_model: vehicleModel === "all" ? "" : vehicleModel,
    } })).data,
    placeholderData: keepPreviousData,
  });
  const { data: facets = { vehicle_makes: [], vehicle_models: [] } } = useQuery({
    queryKey: ["product-facets"], queryFn: async () => (await api.get("/products/facets")).data,
  });
  const vehicleModels = facets.vehicle_models.filter((item) => vehicleMake === "all" || item.make === vehicleMake);
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const changeSort = (field) => {
    if (field === sort) setOrder((value) => value === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("asc"); }
    setPage(1);
  };

  const doAdjust = async () => {
    try {
      await api.post("/inventory/adjust", { product_id: adj.product_id, quantity: adj.quantity, reason: adj.reason });
      toast.success(t("g2.action_done")); setAdj(null);
      qc.invalidateQueries({ queryKey: ["balances"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <PageHeader title={t("nav.inventory")} subtitle={t("g2.inventory_sub")}
        actions={can("inventory:adjust") && (
          <Button variant="outline" onClick={() => navigate("/data-import/stock")} data-testid="import-stock-button">
            <Upload className="h-4 w-4" />{t("data_import.import")}
          </Button>
        )} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} placeholder={t("common.search")} className="pl-9" data-testid="inventory-search-input" />
        </div>
        <Select value={status} onValueChange={(value) => { setStatus(value); setPage(1); }}>
          <SelectTrigger className="w-40" aria-label={t("common.status")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="true">{t("common.active")}</SelectItem><SelectItem value="false">{t("common.inactive")}</SelectItem><SelectItem value="all">{t("common.all")}</SelectItem></SelectContent>
        </Select>
        <Select value={category} onValueChange={(value) => { setCategory(value); setPage(1); }}>
          <SelectTrigger className="w-52" aria-label={t("products.category")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_categories")}</SelectItem>{PRODUCT_CATEGORIES.map((value) => <SelectItem key={value} value={value}>{categoryLabel(t, value)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vehicleMake} onValueChange={(value) => { setVehicleMake(value); setVehicleModel("all"); setPage(1); }}>
          <SelectTrigger className="w-48" aria-label={t("products.vehicle_make")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_vehicle_makes")}</SelectItem>{facets.vehicle_makes.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vehicleModel} onValueChange={(value) => { setVehicleModel(value); setPage(1); }}>
          <SelectTrigger className="w-48" aria-label={t("products.vehicle_model")}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("products.all_vehicle_models")}</SelectItem>{vehicleModels.map((item) => <SelectItem key={`${item.make}-${item.model}`} value={item.model}>{item.make} {item.model}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
          <SelectTrigger className="w-28" aria-label="Einträge pro Seite"><SelectValue /></SelectTrigger>
          <SelectContent>{[25, 50, 100].map((size) => <SelectItem key={size} value={String(size)}>{size} / Seite</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Card className="overflow-hidden">
        {isLoading ? <div className="p-6 text-sm text-slate-400">{t("common.loading")}</div>
          : data.items.length === 0 ? <EmptyState icon={Sliders} title={t("g2.balances")} testId="inventory-empty" />
          : (
          <Table containerClassName="max-h-[65vh]" data-testid="inventory-table">
            <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background"><TableRow><SortableHead field="sku" current={sort} order={order} onSort={changeSort}>{t("products.sku")}</SortableHead><SortableHead field="name" current={sort} order={order} onSort={changeSort}>{t("common.name")}</SortableHead><SortableHead field="category" current={sort} order={order} onSort={changeSort}>{t("products.category")}</SortableHead><SortableHead field="vehicle_make" current={sort} order={order} onSort={changeSort} className="hidden xl:table-cell">{t("automotive.vehicle")}</SortableHead><SortableHead field="warehouse" current={sort} order={order} onSort={changeSort} className="hidden md:table-cell">{t("nav.inventory")}</SortableHead><SortableHead field="quantity" current={sort} order={order} onSort={changeSort} className="text-right">{t("g2.quantity")}</SortableHead><SortableHead field="reserved" current={sort} order={order} onSort={changeSort} className="hidden text-right lg:table-cell">{t("g2.reserved")}</SortableHead><SortableHead field="available" current={sort} order={order} onSort={changeSort} className="text-right">{t("g2.available")}</SortableHead><SortableHead field="is_active" current={sort} order={order} onSort={changeSort}>{t("common.status")}</SortableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((b) => (
                <TableRow key={b.id} data-testid={`balance-row-${b.product_id}`}>
                  <TableCell className="font-medium tabular-nums">{b.sku}</TableCell>
                  <TableCell>{b.product_name}</TableCell>
                  <TableCell>{categoryLabel(t, b.category)}</TableCell>
                  <TableCell className="hidden xl:table-cell">{b.vehicle_make ? `${b.vehicle_make}${b.vehicle_model ? ` ${b.vehicle_model}` : ""}` : "—"}</TableCell>
                  <TableCell className="hidden md:table-cell">{b.warehouse_code || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNumber(b.quantity, 0)}</TableCell>
                  <TableCell className="hidden text-right tabular-nums text-amber-600 lg:table-cell">{fmtNumber(b.reserved, 0)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtNumber(b.available, 0)}</TableCell>
                  <TableCell><StatusBadge status={b.is_active ? "active" : "inactive"} labelKey={b.is_active ? "common.active" : "common.inactive"} /></TableCell>
                  <TableCell className="text-right">
                    {can("inventory:adjust") && <Button variant="ghost" size="sm" onClick={() => setAdj({ product_id: b.product_id, name: b.product_name, quantity: "", reason: "" })} data-testid={`adjust-btn-${b.product_id}`}>{t("g2.adjust")}</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {data.total > pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{t("common.total")}: {data.total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => setPage((value) => value - 1)} data-testid="inventory-prev">{t("common.previous")}</Button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isFetching} onClick={() => setPage((value) => value + 1)} data-testid="inventory-next">{t("common.next")}</Button>
          </div>
        </div>
      )}

      <Dialog open={!!adj} onOpenChange={(v) => !v && setAdj(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("g2.adjust")}{adj ? ` · ${adj.name}` : ""}</DialogTitle></DialogHeader>
          {adj && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>{t("g2.quantity")} (+/-)</Label><Input type="number" step="1" value={adj.quantity} onChange={(e) => setAdj({ ...adj, quantity: e.target.value })} data-testid="adjust-qty-input" /></div>
              <div className="space-y-1.5"><Label>{t("g2.reason")} *</Label><Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="adjust-reason-input" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={doAdjust} disabled={!adj?.reason || !adj?.quantity} data-testid="adjust-save-button">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
